import { v4 as uuid } from "uuid";
import {
  ProgramCorporateEndorsement,
  ProgramIndividualEndorsement,
} from "@/common/program/types";
import sortBySortValue from "@/utils/functions/sortBySortValue";
import ContentBlock from "@/components/ContentBlock";
import useTranslations from "@/i18n/useTranslations";

function CorporateEndorsementItem({
  endorsement,
  key,
  isInSideRail = false,
}: {
  endorsement: ProgramCorporateEndorsement;
  key: string;
  isInSideRail?: boolean;
}) {
  if (endorsement.individualEndorsements.length === 0) {
    return null;
  }
  const individualEndorsement = endorsement.individualEndorsements[0];
  const endorser =
    individualEndorsement.endorser.length > 0
      ? individualEndorsement.endorser[0]
      : null;
  const image =
    endorsement.image.length > 0 && !isInSideRail ? endorsement.image[0] : null;
  const fullName =
    `${endorser?.givenName || ""} ${endorser?.familyName || ""}`.trim();
  const attribution = [fullName, endorser?.title].filter(Boolean).join(", ");

  return (
    <div key={key} className="flex flex-row gap-4 justify-center items-start">
      <ContentBlock
        variant="quoted"
        imageurl={image?.src || ""}
        imagetitle={endorsement.corporationName || ""}
        className={isInSideRail ? "rounded-[8px]" : ""}
      >
        <div className="mb-0" style={{ lineHeight: "1.925rem" }}>
          <p
            className={`${isInSideRail ? "text-md mb-1" : "text-lg leading-8"} text-gray-800`}
          >
            {individualEndorsement.quote}
          </p>
          <p
            className={`font-bold ${isInSideRail ? "italic text-md" : "text-lg"}`}
          >
            {isInSideRail ? "- " : ""} {attribution}
          </p>
        </div>
      </ContentBlock>
    </div>
  );
}

function IndividualEndorsementItem({
  endorsement,
  key,
  isInSideRail = false,
}: {
  endorsement: ProgramIndividualEndorsement;
  key: string;
  isInSideRail?: boolean;
}) {
  if (!endorsement.quote || endorsement.quote.trim().length === 0) {
    return null;
  }

  return (
    <div key={key} className="flex flex-row gap-4 justify-center items-start">
      <ContentBlock
        variant="quoted"
        imageurl=""
        imagetitle=""
        className={isInSideRail ? "rounded-[8px]" : ""}
      >
        <div className="mb-0" style={{ lineHeight: "1.925rem" }}>
          <p
            className={`${isInSideRail ? "text-md mb-1" : "text-lg leading-8"} text-gray-800`}
          >
            {endorsement.quote}
          </p>
          <p
            className={`font-bold ${isInSideRail ? "italic text-md" : "text-lg"}`}
          >
            {isInSideRail ? "- " : ""}{" "}
            {endorsement.endorser?.[0]?.givenName || ""}{" "}
            {endorsement.endorser?.[0]?.familyName || ""}
          </p>
        </div>
      </ContentBlock>
    </div>
  );
}

interface ProgramEndorsementsProps {
  corporateEndorsements: ProgramCorporateEndorsement[];
  individualEndorsements?: ProgramIndividualEndorsement[];
  isInSideRail?: boolean;
}

function ProgramEndorsements({
  corporateEndorsements = [],
  individualEndorsements = [],
  isInSideRail = false,
}: ProgramEndorsementsProps) {
  const t = useTranslations("Program");
  const hasCorporateEndorsements = corporateEndorsements.length > 0;
  const hasIndividualEndorsements = individualEndorsements.length > 0;

  if (!hasCorporateEndorsements && !hasIndividualEndorsements) {
    return null;
  }

  const corporateQuotes = new Set(
    corporateEndorsements
      .flatMap((corp) => corp.individualEndorsements)
      .map((ind) => ind.quote?.trim())
      .filter((quote): quote is string => Boolean(quote)),
  );

  const displayCorporateEndorsements = isInSideRail
    ? corporateEndorsements.slice(0, 1)
    : corporateEndorsements;

  const displayIndividualEndorsements = isInSideRail
    ? individualEndorsements.slice(0, 1)
    : individualEndorsements;

  return (
    <div className="mx-auto bg-inherit flex flex-col gap-4">
      {!isInSideRail && (
        <h2 className="text-primary-500 font-bold md:text-3xl mt-0 mb-0 text-left">
          {t("programEndorsements")}
        </h2>
      )}
      {hasCorporateEndorsements &&
        sortBySortValue(displayCorporateEndorsements).map((endorsement) => {
          return (
            <CorporateEndorsementItem
              key={uuid()}
              endorsement={endorsement}
              isInSideRail={isInSideRail}
            />
          );
        })}
      {hasIndividualEndorsements &&
        displayIndividualEndorsements
          .filter(
            (endorsement) =>
              endorsement.quote &&
              endorsement.quote.trim().length > 0 &&
              !corporateQuotes.has(endorsement.quote.trim()),
          )
          .map((endorsement) => {
            return (
              <IndividualEndorsementItem
                key={uuid()}
                endorsement={endorsement}
                isInSideRail={isInSideRail}
              />
            );
          })}
    </div>
  );
}

export default ProgramEndorsements;
